<template>
  <component
    :is="tag"
    ref="_ref"
    :class="[
      ns.b(),
      ns.m(_type),
      ns.m(_size),
      ns.is('disabled', _disabled),
      ns.is('loading', loading),
      ns.is('rounded', _rounded),
      ns.is('icon', Boolean(icon || $slots.icon)),
    ]"
    :aria-disabled="_disabled || loading"
    :disabled="_disabled || loading"
    :autofocus="autofocus"
    :type="nativeType"
    :data-test-name="Boolean(icon || $slots.icon) ? 'button-icon' : 'button'"
    :data-test-variant="_type"
    data-test-state=""
    @click="handleClick"
  >
    <template v-if="loading">
      <slot v-if="$slots.loading" name="loading" />
      <b-icon v-else :class="ns.is('loading')">
        <component :is="loadingIcon" />
      </b-icon>
    </template>
    <b-icon v-if="icon || $slots.icon">
      <component :is="icon" v-if="icon" />
      <slot v-else name="icon" />
    </b-icon>
    <template v-else>
      <b-icon v-if="prefixIcon || $slots.prefixIcon">
        <component :is="prefixIcon" v-if="prefixIcon" />
        <slot v-else name="prefixIcon" />
      </b-icon>
      <span>
        <slot />
      </span>
      <b-icon v-if="suffixIcon || $slots.suffixIcon">
        <component :is="suffixIcon" v-if="suffixIcon" />
        <slot v-else name="suffixIcon" />
      </b-icon>
    </template>
  </component>
</template>

<script lang="ts" setup>
import { computed, inject, ref } from 'vue'
import { BIcon } from '@bigin/components/icon'
import { useDisabled, useNamespace, useSize } from '@bigin/hooks'
import { buttonGroupContextKey } from '@bigin/tokens'
import { buttonEmits, buttonProps } from './button'

defineOptions({
  name: 'BButton',
})

const props = defineProps(buttonProps)
const emit = defineEmits(buttonEmits)

const buttonGroupContext = inject(buttonGroupContextKey, undefined)
const ns = useNamespace('button')
const _size = useSize(
  computed(
    () =>
      (props.xlarge && 'xlarge') ||
      (props.large && 'large') ||
      (props.small && 'small') ||
      (props.xsmall && 'xsmall') ||
      (props.xxsmall && 'xxsmall') ||
      props.size ||
      buttonGroupContext?.size
  )
)
const _disabled = useDisabled()
const _ref = ref<HTMLButtonElement>()

const _type = computed(
  () =>
    (props.primary && 'primary') ||
    (props.secondary && 'secondary') ||
    (props.ghost && 'ghost') ||
    (props.danger && 'danger') ||
    (props.semiDanger && 'semi-danger') ||
    props.type ||
    buttonGroupContext?.type ||
    ''
)

const _rounded = computed(() => props.rounded || buttonGroupContext?.rounded)

// const buttonStyle = useButtonCustomStyle(props)

const handleClick = (evt: MouseEvent) => {
  emit('click', evt)
}

defineExpose({
  /** @description button html element */
  ref: _ref,
  /** @description button size */
  size: _size,
  /** @description button type */
  type: _type,
  /** @description button disabled */
  disabled: _disabled,
  /** @description button rounded */
  rounded: _rounded,
})
</script>
